import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useLocation } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import AlertModal from "../../../components/alertModal/alertModal";
import CustomBreadcrumb from "../../../components/customBreadcrumb/CustomBreadcrumb";
import EmptyPlaceholder from "../../../components/emptyPlaceholder/EmptyPlaceholder";
import MainLoader from "../../../components/loading/MainLoader";
import SearchBar from "../../../components/searchBar/SearchBar";
import Title from "../../../components/title/Title";
import { getNewServiceOrder } from "../../../services/fault";
import inProgressIcon from "../../../assets/images/icons/faultProgress/loading sc.gif";
import completedIcon from "../../../assets/images/icons/faultProgress/completed.svg";
import disabledIcon from "../../../assets/images/icons/faultProgress/disabled.svg";
import styles from "./ServiceRequest.module.scss";
import useMediaQuery from "@mui/material/useMediaQuery";
import { IServiceDetails } from "../../../interfaces/serviceDetails";

const ServiceRequestProgress = () => {
  const { state } = useLocation();
  const isMobile = useMediaQuery("(max-width: 600px)");
  const [loading, setLoading] = useState<boolean>(false);
  const [orderDetails, setOrderDetails] = useState<IServiceDetails>();
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined
  );
  const [searchFaultId, setSearchFaultId] = useState<string>();
  const [forceSearch, setForceSearch] = useState<boolean>(false);

  useEffect(() => {
    if (state) {
      setSearchFaultId((state as any).srNumber);
    }
  }, [state]);

  useEffect(() => {
    if (searchFaultId) {
      setOrderDetails(undefined);

      setLoading(true);
      getNewServiceOrder(searchFaultId.trim())
        .then((data: IServiceDetails) => {
          setOrderDetails(data);
          setLoading(false);
        })
        .catch((err) => {
          setErrorMessage(err.message);
          setLoading(false);
        });
    }
  }, [searchFaultId, forceSearch]);

  const searchHandler = (value: string) => {
    if (!value) {
      return;
    }

    // setorderDetails(undefined);

    setSearchFaultId(value);
    setForceSearch((prev) => !prev);
  };

  interface Step {
    number: number;
    title: string;
    description?: string;
    active: boolean;
    icon?: JSX.Element;
  }

  const steps: Step[] = [
    {
      number: 1,
      title: "Service request created",
      description: orderDetails?.submittedDate
        ? new Date(orderDetails.submittedDate).toLocaleDateString()
        : undefined,
      active: !!orderDetails?.status,
      icon: (
        <img
          className={styles.resizedGif}
          src={!!orderDetails?.status ? completedIcon : disabledIcon}
          alt="Created"
        />
      ),
    },
    {
      number: 2,
      title: "Acknowledged",
      active:
        !!orderDetails?.status?.includes("COMPLETED") ||
        !!orderDetails?.status?.includes("IN_PROGRESS") ||
        !!orderDetails?.status?.includes("ACKNOWLEDGED"),
      icon: (
        <img
          className={styles.resizedGif}
          src={
            orderDetails?.status?.includes("COMPLETED")
              ? completedIcon
              : orderDetails?.status?.includes("IN_PROGRESS")
              ? completedIcon
              : orderDetails?.status?.includes("ACKNOWLEDGED")
              ? completedIcon
              : disabledIcon
          }
          alt="Assigned"
        />
      ),
    },
    {
      number: 3,
      title: "Work in progress",
      active:
        !!orderDetails?.status?.includes("COMPLETED") ||
        !!orderDetails?.status?.includes("IN_PROGRESS"),
      icon: (
        <img
          className={styles.resizedGif}
          src={
            orderDetails?.status?.includes("COMPLETED")
              ? completedIcon
              : orderDetails?.status?.includes("IN_PROGRESS")
              ? inProgressIcon
              : disabledIcon
          }
          alt="Resolved"
        />
      ),
    },
    {
      number: 4,
      title: "Completed",
      active: !!orderDetails?.status?.includes("COMPLETED"),
      icon: (
        <img
          className={styles.resizedGif}
          src={
            orderDetails?.status?.includes("COMPLETED")
              ? completedIcon
              : disabledIcon
          }
          alt="Resolved"
        />
      ),
    },
  ];

  return (
    <div className="page-container">
      <MainLoader show={loading} />
      <CustomBreadcrumb
        items={[
          { path: "/", title: "Home" },
          {
            path: "/tracker/status/serviceRequestProgress",
            title: "Service Request Status",
          },
        ]}
      />
      <Title>Service Request Status</Title>
      <Row className="justify-content-center mt-2">
        <Col className="col-md-5">
          <SearchBar
            placeholder="Service Request Reference"
            onSearch={searchHandler}
            defaultValue={searchFaultId}
          />
        </Col>
      </Row>

      <Row>
        <Col className={[styles.container, "mt-2 mb-4 py-5 px-5"].join(" ")}>
          {orderDetails ? (
            <Box
              display="flex"
              flexDirection={isMobile ? "column" : "row"}
              justifyContent="center"
              alignItems="center"
              gap={2}
              mt={4}
              sx={{
                flexWrap: "wrap",
              }}
            >
              {steps.map((step, index) => (
                <Box
                  key={index}
                  display="flex"
                  alignItems="center"
                  justifyContent="flex-start"
                  width={isMobile ? "100%" : "260px"}
                  height={isMobile ? "auto" : "110px"}
                  borderRadius="15px"
                  padding="10px 20px"
                  sx={{
                    background: step.active
                      ? "linear-gradient(to right, #D4EAF7, #F2F8FF)"
                      : "#E5E5E5",
                    color: step.active ? "#0A3D91" : "#A0A0A0",
                    boxShadow: step.active ? 3 : 0,
                    transition: "0.3s",
                  }}
                >
                  <Box
                    borderRadius="50%"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    m={isMobile ? 1 : 1}
                    mr={isMobile ? 2 : 1}
                    sx={{
                      backgroundColor: step.active ? "#51B848" : "#BDBDBD",
                      color: "white",
                      fontSize:
                        step.number === 2 || step.number === 3
                          ? "16px"
                          : "18px",
                      fontWeight: "bold",
                    }}
                  >
                    {step.icon}
                  </Box>

                  <Box
                    width="3px"
                    height="35px"
                    mx={2}
                    sx={{
                      backgroundColor: step.active ? "#0A3D91" : "#A0A0A0",
                    }}
                  />

                  <Box>
                    <Typography
                      fontWeight={step.active ? "bold" : "normal"}
                      fontSize={isMobile ? "12px" : "14px"}
                      sx={{ color: step.active ? "#0A3D91" : "#A0A0A0" }}
                    >
                      {step.title}
                    </Typography>
                    {step.description && (
                      <Typography
                        fontSize={isMobile ? "10px" : "12px"}
                        sx={{ color: step.active ? "#0A3D91" : "#A0A0A0" }}
                      >
                        {step.description}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          ) : (
            <EmptyPlaceholder />
          )}
        </Col>
      </Row>

      {/* error message */}
      <AlertModal
        show={!!errorMessage}
        buttons={[
          {
            variant: "danger",
            text: "OK",
            onClick: () => {
              setErrorMessage(undefined);
            },
          },
        ]}
        variant={"error"}
      >
        {errorMessage}
      </AlertModal>
    </div>
  );
};

export default ServiceRequestProgress;
